import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeftIcon, DocumentTextIcon, PlusIcon, TrashIcon, CpuChipIcon } from '@heroicons/react/24/outline';
import { getTeamUuid } from '../../../../utils/cookies';
import { getResearchPipeline, retryPipelineStep, cancelPipeline, deletePipeline, createResearchPipeline } from '../../../../Requests/ResearchPipelineRequests';
import SectionHeader from '../../../../Components/GeneralComponents/SectionHeader';
import PipelineProgressTracker from '../../../../Components/ResearchPipeline/PipelineProgressTracker';
import PipelineActionsMenu from '../../../../Components/ResearchPipeline/PipelineActionsMenu';
import DeletePipelineModal from '../../../../Components/ResearchPipeline/DeletePipelineModal';
import { 
    PIPELINE_STATUSES, 
    STEP_STATUSES,
    STEP_DETAILS,
    formatDateTime, 
    getStatusColor,
    canCancelPipeline 
} from '../../../../utils/pipelineHelpers';
import Loader from '../../../../Components/Loader';
import { useSidebar } from '../../../../Components/ContextProviders/SidebarContextProvider';
import { getSidebarPadding } from '../../../../utils/sidebar';

function PipelineDetail() {
    const { pipeline_uuid } = useParams();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { isCollapsed } = useSidebar();
    const [pipeline, setPipeline] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [actionLoading, setActionLoading] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [isRetrying, setIsRetrying] = useState(false);

    const loadPipeline = useCallback(async (showLoading = true) => {
        try {
            if (showLoading) setLoading(true);
            setError(null);
            
            const teamUuid = getTeamUuid();
            const response = await getResearchPipeline(pipeline_uuid, teamUuid);
            
            if (response.data.success) {
                setPipeline(response.data.pipeline);
            } else {
                setError(t('ResearchPipeline.pipelineNotFound'));
            }
        } catch (err) {
            console.error('Error loading pipeline:', err);
            if (err.response?.status === 404) {
                setError(t('ResearchPipeline.pipelineNotFound'));
            } else {
                setError(t('ResearchPipeline.failedLoadPipelineDetails'));
            }
        } finally {
            if (showLoading) setLoading(false);
        }
    }, [pipeline_uuid]);

    useEffect(() => {
        loadPipeline();
        
        // Set up polling for active pipelines
        const pollInterval = setInterval(() => {
            if (pipeline && [PIPELINE_STATUSES.PENDING, PIPELINE_STATUSES.PROCESSING].includes(pipeline.status)) {
                loadPipeline(false); // Silent reload
            }
        }, 3000);

        return () => clearInterval(pollInterval);
    }, [loadPipeline, pipeline?.status]);

    const handleRetryStep = async (stepName) => {
        try {
            setActionLoading(true);
            const teamUuid = getTeamUuid();
            const response = await retryPipelineStep(pipeline.uuid, stepName, teamUuid);
            
            if (response.data.success) {
                await loadPipeline(false);
            } else {
                alert(t('ResearchPipeline.failedRetryStepTryAgain'));
            }
        } catch (error) {
            console.error('Error retrying step:', error);
            alert(t('ResearchPipeline.failedRetryStepTryAgain'));
        } finally {
            setActionLoading(false);
        }
    };

    const handleCancelPipeline = async () => {
        if (!window.confirm('Are you sure you want to cancel this pipeline? This action cannot be undone.')) {
            return;
        }
        
        try {
            setActionLoading(true);
            const teamUuid = getTeamUuid();
            const response = await cancelPipeline(pipeline.uuid, teamUuid);
            
            if (response.data.success) {
                await loadPipeline(false);
            } else {
                alert(t('ResearchPipeline.failedCancelPipelineTryAgain'));
            }
        } catch (error) {
            console.error('Error cancelling pipeline:', error);
            alert(t('ResearchPipeline.failedCancelPipelineTryAgain'));
        } finally {
            setActionLoading(false);
        }
    };

    const handleDeletePipeline = async () => {
        try {
            setIsDeleting(true);
            const teamUuid = getTeamUuid();
            const response = await deletePipeline(pipeline.uuid, teamUuid);
            
            if (response.data.success) {
                navigate('/research-pipeline');
            } else {
                alert(t('ResearchPipeline.failedDeletePipelineTryAgain'));
            }
        } catch (error) {
            console.error('Error deleting pipeline:', error);
            alert(t('ResearchPipeline.failedDeletePipelineTryAgain'));
        } finally {
            setIsDeleting(false);
            setShowDeleteModal(false);
        }
    };

    const handleRetryPipeline = async () => {
        if (!pipeline) return;
        
        setIsRetrying(true);
        try {
            // Create a new pipeline with the same configuration
            const formData = new FormData();
            formData.append('name', `${pipeline.name} (Retry)`);
            formData.append('research_description', pipeline.research_description || '');
            formData.append('team_uuid', getTeamUuid());
            formData.append('project_uuid', pipeline.project?.uuid);
            
            // Add other pipeline configuration data if available
            if (pipeline.config) {
                Object.keys(pipeline.config).forEach(key => {
                    if (pipeline.config[key] !== null && pipeline.config[key] !== undefined) {
                        formData.append(key, pipeline.config[key]);
                    }
                });
            }

            const response = await createResearchPipeline(formData);
            if (response.data?.success && response.data?.pipeline) {
                // Navigate to the new pipeline
                navigate(`/research-pipeline/${response.data.pipeline.uuid}`);
            } else {
                alert(t('ResearchPipeline.failedRetryPipelineTryAgain'));
            }
        } catch (error) {
            console.error('Error retrying pipeline:', error);
            alert(t('ResearchPipeline.failedRetryPipelineTryAgain'));
        } finally {
            setIsRetrying(false);
        }
    };

    const handleBack = () => {
        navigate('/research-pipeline');
    };

    const handleCreateNew = () => {
        navigate('/research-pipeline/create');
    };

    const getStatusBadge = (status) => {
        const colorClasses = getStatusColor(status);
        const statusText = status.charAt(0).toUpperCase() + status.slice(1);
        
        return (
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${colorClasses}`}>
                {statusText}
            </span>
        );
    };

    if (loading) {
        return (
            <div className={`${getSidebarPadding(isCollapsed)} transition-all duration-300 py-2 md:py-12 w-full flex justify-center items-center`}>
                <Loader />
            </div>
        );
    }

    if (error) {
        return (
            <div className={`${getSidebarPadding(isCollapsed)} transition-all duration-300 py-2 md:py-12 w-full flex flex-col gap-4 items-center px-4`}>
                <div className="bg-white max-w-7xl rounded-2xl w-full h-full shadow-xl p-6">
                    <div className="text-center py-12">
                        <div className="text-red-400 text-6xl mb-4">⚠️</div>
                        <h3 className="text-lg font-medium text-gray-900 mb-2">
                            {error}
                        </h3>
                        <p className="text-gray-500 mb-6">
                            The pipeline you're looking for could not be found or loaded.
                        </p>
                        <button
                            onClick={handleBack}
                            className="bg-iavisus-100 text-white px-6 py-3 rounded-lg hover:bg-iavisus-200 transition-colors"
                        >
                            Back to Pipelines
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // Safety check to prevent render errors
    if (!pipeline) {
        return (
            <div className={`${getSidebarPadding(isCollapsed)} transition-all duration-300 py-2 md:py-12 w-full flex justify-center items-center`}>
                <Loader />
            </div>
        );
    }

    // Always use simplified UI - just show progress bar with status colors
    return (
        <div className={`${getSidebarPadding(isCollapsed)} transition-all duration-300 py-2 md:py-12 w-full flex flex-col gap-4 items-center px-4`}>
            <div className="flex flex-col w-full justify-center items-center gap-8 md:px-4">
                <div className="bg-white max-w-7xl rounded-2xl w-full h-full shadow-xl relative">
                    <div className="p-12">
                        {/* Simple back button */}
                        <div className="mb-8">
                            <button
                                onClick={handleBack}
                                className="text-gray-400 hover:text-gray-600 transition-colors flex items-center gap-2"
                            >
                                <ArrowLeftIcon className="w-4 h-4" />
                                Back
                            </button>
                        </div>

                        {/* Pipeline name and description layout */}
                        <div className="flex justify-between items-start mb-12">
                            {/* Left side - Pipeline name and status */}
                            <div className="flex-1">
                                <h1 className="text-2xl font-bold text-gray-900 mb-2">
                                    {pipeline.name}
                                </h1>
                                <p className="text-gray-500 text-sm">
                                    {pipeline.status === PIPELINE_STATUSES.PROCESSING ? t('ResearchPipeline.pipelineInProgress') :
                                     pipeline.status === PIPELINE_STATUSES.PENDING ? t('ResearchPipeline.pipelineStarting') :
                                     pipeline.status === PIPELINE_STATUSES.COMPLETED ? t('ResearchPipeline.pipelineCompleted') :
                                     pipeline.status === PIPELINE_STATUSES.FAILED ? t('ResearchPipeline.pipelineFailed') :
                                     pipeline.status === PIPELINE_STATUSES.CANCELLED ? t('ResearchPipeline.pipelineCancelled') :
                                     t('ResearchPipeline.title')}
                                </p>
                            </div>
                            
                            {/* Right side - Actions Menu */}
                            <div className="flex items-center gap-4">
                                <PipelineActionsMenu
                                    pipeline={pipeline}
                                    onRetryStep={handleRetryStep}
                                    onRetryPipeline={handleRetryPipeline}
                                    onDelete={() => setShowDeleteModal(true)}
                                    disabled={actionLoading || isRetrying}
                                />
                            </div>
                        </div>

                        {/* Progress Tracker and Current Task Layout */}
                        <div className="flex flex-col lg:flex-row lg:justify-between items-center lg:items-start gap-8 mb-24">
                            {/* Progress Tracker - centered on mobile, left on desktop */}
                            <div className="flex-shrink-0">
                                {pipeline && pipeline.steps ? (
                                    <PipelineProgressTracker
                                        pipeline={pipeline}
                                        onRetryStep={handleRetryStep}
                                        disabled={actionLoading}
                                    />
                                ) : (
                                    <div className="text-gray-500">
                                        Loading pipeline steps...
                                    </div>
                                )}
                            </div>

                            {/* Current Task Box or Completion Message */}
                            {(pipeline.status === PIPELINE_STATUSES.PROCESSING || pipeline.status === PIPELINE_STATUSES.PENDING || pipeline.status === PIPELINE_STATUSES.COMPLETED) && (
                                <div className="w-full max-w-[540px] lg:w-[540px] h-auto bg-gray-50 border border-gray-200 rounded-lg p-6 lg:p-10">
                                    <div>
                                        {/* Completion Message */}
                                        {pipeline.status === PIPELINE_STATUSES.COMPLETED ? (
                                            <div>
                                                <h3 className="text-xl lg:text-2xl font-semibold text-gray-900 mb-4 lg:mb-6">
                                                    {t('ResearchPipeline.pipelineCompleted')}
                                                </h3>
                                                <p className="text-gray-600 text-sm lg:text-base leading-relaxed mb-6 lg:mb-8">
                                                    {t('ResearchPipeline.pipelineCompletedDesc')} {t('ResearchPipeline.seeResultsDesc')}
                                                </p>
                                                
                                                {/* Total Pipeline Cost */}
                                                <div className="border-t border-gray-200 pt-4 lg:pt-6">
                                                    <div className="flex items-center justify-between">
                                                        <span className="text-sm lg:text-base font-medium text-gray-900">
                                                            {t('ResearchPipeline.totalPipelineCost')}:
                                                        </span>
                                                        <div className="flex items-center gap-2">
                                                            <CpuChipIcon className="w-4 h-4 text-gray-600" />
                                                            <span className="text-sm lg:text-base font-semibold text-gray-900">
                                                                {pipeline.actual_cost ? Number(pipeline.actual_cost).toFixed(2) : '0.00'}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        ) : (
                                            /* In-Progress Task Content */
                                            <>
                                                <h3 className="text-xl lg:text-2xl font-semibold text-gray-900 mb-4 lg:mb-6">
                                                    {(() => {
                                                        const currentStep = pipeline.steps.find(step => 
                                                            step.status === STEP_STATUSES.PROCESSING || 
                                                            (step.status === STEP_STATUSES.PENDING && !pipeline.steps.some(s => s.status === STEP_STATUSES.PROCESSING))
                                                        );
                                                        const stepInfo = STEP_DETAILS[currentStep?.step_name];
                                                        return t(`PipelineResults.pipelineSteps.${currentStep.step_name}.name`) || stepInfo?.name || currentStep?.step_name || t('ResearchPipeline.preparingPipeline');
                                                    })()}
                                                </h3>
                                                <p className="text-gray-600 text-sm lg:text-base leading-relaxed mb-6 lg:mb-8">
                                                    {(() => {
                                                        const currentStep = pipeline.steps.find(step => 
                                                            step.status === STEP_STATUSES.PROCESSING || 
                                                            (step.status === STEP_STATUSES.PENDING && !pipeline.steps.some(s => s.status === STEP_STATUSES.PROCESSING))
                                                        );
                                                        const stepInfo = STEP_DETAILS[currentStep?.step_name];
                                                        return t(`PipelineResults.pipelineSteps.${currentStep.step_name}.description`) || stepInfo?.description || t('ResearchPipeline.preparingPipelineDesc');
                                                    })()}
                                                </p>
                                                
                                                {/* Step result description without title */}
                                                <div className="border-t border-gray-200 pt-4 lg:pt-6">
                                                    <p className="text-gray-600 text-sm lg:text-base leading-relaxed">
                                                        {(() => {
                                                            const currentStep = pipeline.steps.find(step => 
                                                                step.status === STEP_STATUSES.PROCESSING || 
                                                                (step.status === STEP_STATUSES.PENDING && !pipeline.steps.some(s => s.status === STEP_STATUSES.PROCESSING))
                                                            );
                                                            const stepName = currentStep?.step_name;
                                                            
                                                            return t(`PipelineResults.stepResults.${stepName}`) || t('PipelineResults.stepResults.default');
                                                        })()}
                                                    </p>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Bottom content - buttons for completed or message for in-progress */}
                        <div className="absolute bottom-8 left-8 right-8">
                            {pipeline.status === PIPELINE_STATUSES.COMPLETED ? (
                                <div className="flex justify-between">
                                    <button
                                        onClick={() => pipeline.project && navigate(`/project/${pipeline.project.uuid}`)}
                                        disabled={!pipeline.project}
                                        className="px-6 py-3 border border-iavisus-100 text-iavisus-100 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
                                    >
                                        {t('ResearchPipeline.viewProject')}
                                    </button>
                                    <button
                                        onClick={() => navigate(`/research-pipeline/${pipeline.uuid}/results`)}
                                        className="px-6 py-3 bg-iavisus-100 text-white rounded-lg hover:bg-iavisus-200 transition-colors"
                                    >
                                        {t('ResearchPipeline.seeResults')}
                                    </button>
                                </div>
                            ) : (pipeline.status === PIPELINE_STATUSES.PROCESSING || pipeline.status === PIPELINE_STATUSES.PENDING) && (
                                <div className="text-center">
                                    <p className="text-gray-600 text-sm">
                                        {t('ResearchPipeline.processingWarning')}{' '}
                                        <a 
                                            href="mailto:support@aiqda.com" 
                                            className="text-black underline hover:text-gray-700 transition-colors"
                                        >
                                            {t('ResearchPipeline.supportEmail')}
                                        </a>
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Delete Pipeline Modal */}
            <DeletePipelineModal
                isOpen={showDeleteModal}
                onClose={() => setShowDeleteModal(false)}
                onConfirm={handleDeletePipeline}
                pipelineName={pipeline?.name}
                isDeleting={isDeleting}
            />
        </div>
    );
}

export default PipelineDetail;